import { useMemo } from 'react';
import { SelectProps } from '@radix-ui/react-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import { Text } from '@antimatterhq/ui/components/typography';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';

export type PreTransformPresetsDropdownProps = Pick<
  SelectProps,
  'value' | 'onValueChange' | 'disabled'
> & {
  onBlur?: () => void;
  name?: string | null;
  'data-testid'?: string;
};

const Preset = ({ name }: { name?: string | null }) => {
  return <Text>{name}</Text>;
};

export default function PreTransformPresetsDropdown({
  value,
  onValueChange,
  onBlur,
  name,
  'data-testid': testId,
  ...props
}: PreTransformPresetsDropdownProps) {
  const { data: presets, isLoading } = useGetDatasourcePreset({ name });

  const names = useMemo(() => {
    return (
      presets?.silver?.preTransform?.map((preset) => preset.name || '') || []
    );
  }, [presets]);

  return (
    <>
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={!name}
        data-testid={testId}
        {...props}
      >
        <SelectTrigger
          onBlur={onBlur}
          className="max-w-form"
          data-testid={`${testId}-select-trigger`}
        >
          <SelectValue
            placeholder="Choose a preset"
            data-testid={`${testId}-select-value`}
          >
            {value && (
              <Preset
                name={value}
                data-testid={`${testId}-select-value-${value}`}
              />
            )}
          </SelectValue>
          <SelectContent>
            {!!names.length && (
              <>
                <SelectItem value="none">
                  <Preset name="none" />
                </SelectItem>
                <SelectSeparator />
              </>
            )}
            {names.map((preset, index) => (
              <SelectItem
                key={preset}
                value={preset}
                data-testid={`${testId}-select-item-${preset}`}
                id={`${testId}-select-item-${index}`}
              >
                <Preset name={preset} />
              </SelectItem>
            ))}
            {!isLoading && !names.length && (
              <div className="p-4 text-center">
                <Text color="info" className="italic">
                  {`The preset "${name}" has no pre-transform presets`}
                </Text>
              </div>
            )}
          </SelectContent>
        </SelectTrigger>
      </Select>
    </>
  );
}
